
import { Button } from "@/components/ui/button";
import {
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";

import { BorderBox } from "@/components/atoms/BorderBox";
import { TooltipButton } from "@/components/atoms/TooltipButton";
import { DroppableContainer } from "@/components/molecules/DroppableContainer";
import { Form } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TooltipProvider } from "@/components/ui/tooltip";
import { CreateCrmWithProjectsResult } from '@/features/crm/hooks/useMutation/useCreateCrmWithProjects/useCreateCrmWithProjects.query';
import { DraggableCrmSystemItem } from "@/features/crm_system/components/molecules/DraggableCrmSystemItem";
import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { CrmSystemIds } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen";
import { OrganizationTypeWordingResolver } from "@/features/organization/components/atoms/OrganizationTypeWordingResolver";
import { dialog, DialogRendererProps } from '@/lib/dynamic-dialog-state';
import { Tables } from "@/lib/supabase/supabase_types_override";
import { useProjectsList, UseProjectsListBaseData, UseProjectsListReturnData } from "@/lib/useQuery/project/useProjectsList";
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox } from "@/components/ui/checkbox";
import { isEqual } from "lodash";
import { ArrowUpDown, TriangleAlert, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useForm, UseFormReturn } from 'react-hook-form';
import z from "zod";
import { connectionsWizardSchema } from './connections-wizard-dialog.schema';
import { useAssignWizardConnections } from "@/features/connection-wizard/hooks/useMutation/useAssignWizardConnections";
import { Spinner } from "@/components/ui/spinner";
import ErrorPopup from "@/components/custom/InfoPopups/ErrorPopup";
import SuccessPopup from "@/components/custom/InfoPopups/SuccessPopup";
import { useGetCrmById } from "@/features/crm/hooks/useQuery/useGetCrmById/useGetCrmById";


type AssignmentDisplay = { id: number; name: string; isForeign?: boolean; foreignCrmName?: string };

export type ConnectionsWizardSchemaType = z.infer<typeof connectionsWizardSchema>;

export interface ConnectionsWizardDialogProps extends DialogRendererProps<CreateCrmWithProjectsResult> {
    connectionWithProjectsAndSurveysConnections?: CreateCrmWithProjectsResult;
    projectsList?: UseProjectsListBaseData;
    isLoading?: boolean;
}

function ConnectionsWizardDialog({ connectionWithProjectsAndSurveysConnections, projectsList, isLoading, ...props }: Readonly<ConnectionsWizardDialogProps>) {


    const { mutateAsync: assignWizardConnections, isPending: isAssigningWizardConnections } = useAssignWizardConnections();

    const form = useForm<ConnectionsWizardSchemaType>({
        resolver: zodResolver(connectionsWizardSchema),
    })

    const hasResetRef = useRef(false);

    useEffect(() => {
        if (hasResetRef.current || !projectsList?.length || !connectionWithProjectsAndSurveysConnections) return;
        hasResetRef.current = true;

        const defaults: ConnectionsWizardSchemaType = {};
        projectsList.forEach((project, index) => {
            const projectConnectionId = project.crm_project_id ?? undefined;

            const surveys: Record<string, { surveyId: string; surveyConnectionId?: number }> = {};
            if (projectConnectionId) {
                project.survey?.forEach((survey, surveyIndex) => {
                    surveys[surveyIndex] = {
                        surveyId: survey.id.toString(),
                        surveyConnectionId: survey.crm_config?.id,
                    };
                });
            }

            defaults[index.toString()] = {
                projectId: project.id.toString(),
                projectConnectionId,
                ...(Object.keys(surveys).length > 0 ? { surveys } : {}),
            };
        });

        form.reset(defaults);
    }, [projectsList, connectionWithProjectsAndSurveysConnections]);

    const onSubmitHandler = async (data: ConnectionsWizardSchemaType) => {
        const defaults = (form.formState.defaultValues ?? {}) as ConnectionsWizardSchemaType;
        const changedData: ConnectionsWizardSchemaType = {};
        Object.entries(data).forEach(([key, value]) => {
            if (!isEqual(value, defaults[key])) {
                changedData[key] = value;
            }
        });

        if (Object.keys(changedData).length === 0) {
            props.closeDialog();
            return;
        }

        await assignWizardConnections({
            crm: connectionWithProjectsAndSurveysConnections!.crm,
            wizardResult: changedData
        })
            .then(() => {
                SuccessPopup("Connections assigned successfully")
            })
            .catch(() => {
                ErrorPopup("An error occurred while assigning connections");
            })
            .finally(() => {
                props.closeDialog()
            })

    }

    console.log(form.formState.errors)
    console.log(form.getValues())
    console.log(!form.formState.isValid )
    console.log(form.formState.isValidating )
    console.log(isLoading)
    console.log( isAssigningWizardConnections)

    const renderContent = () => {
        if (isLoading) {
            return (
                <div className="flex items-center justify-center h-40">
                    <Spinner className='size-6' />
                </div>
            );
        }
        if (!connectionWithProjectsAndSurveysConnections) {
            return (
                <div className="flex items-center justify-center h-40">
                    <p className="text-sm text-muted-foreground">Connection data could not be loaded.</p>
                </div>
            );
        }
        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmitHandler)}>
                    <div className="flex flex-col gap-4 px-1 my-3">
                        <Tabs defaultValue="projects">
                            <TabsList className={`flex h-auto`}>
                                <TabsTrigger className="flex flex-col grow" value="projects">Assign Project Connections</TabsTrigger>
                                <TabsTrigger className="flex flex-col grow" value="surveys">Assign Survey Connections</TabsTrigger>
                            </TabsList>
                            <TabsContent value="projects">
                                <ConnectionsWizardProjectsTabContent
                                    projectsList={projectsList}
                                    form={form}
                                    connectionWithProjectsAndSurveysConnections={connectionWithProjectsAndSurveysConnections}
                                />
                            </TabsContent>
                            <TabsContent value="surveys">
                                <ConnectionsWizardSurveysTabContent
                                    projectsList={projectsList}
                                    form={form}
                                    connectionWithProjectsAndSurveysConnections={connectionWithProjectsAndSurveysConnections}
                                />
                            </TabsContent>
                        </Tabs>
                    </div>
                    <div className="flex justify-end flex-1 w-full gap-2 m-0">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={props.cancel}
                            size='sm'
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            disabled={ isLoading || isAssigningWizardConnections}
                            size='sm'
                        >
                            {isAssigningWizardConnections && <Spinner className='me-2 size-4' />} Finish
                        </Button>
                    </div>
                </form>
            </Form>
        );
    };

    return (
        <DialogContent className='max-w-7xl'>
            <div className="">
                <DialogHeader>
                    <DialogTitle className='m-0 font-bold text-start'>
                        Connections Wizard for: {connectionWithProjectsAndSurveysConnections?.crm.name}
                    </DialogTitle>
                    <DialogDescription>
                        Assign your Connections to Projects and Surveys to enable integration with our system.
                    </DialogDescription>
                </DialogHeader>
                {renderContent()}
            </div>
        </DialogContent>
    )
}

interface ConnectionsWizardTabContentProps {
    projectsList?: UseProjectsListReturnData
    form: UseFormReturn<ConnectionsWizardSchemaType, any, ConnectionsWizardSchemaType>
    connectionWithProjectsAndSurveysConnections: CreateCrmWithProjectsResult
}

function ConnectionsWizardProjectsTabContent({ projectsList, form, connectionWithProjectsAndSurveysConnections }: Readonly<ConnectionsWizardTabContentProps>) {
    const crmProjects = connectionWithProjectsAndSurveysConnections.crm_project;
    const crmSystem = connectionWithProjectsAndSurveysConnections.crm.crm_system;

    const formValues = form.watch();

    const [sortAsc, setSortAsc] = useState(false);
    const [showWithoutConnectionsOnly, setShowWithoutConnectionsOnly] = useState(false);

    const getAssignment = (projectId: string): AssignmentDisplay | undefined => {
        const projectIndex = projectsList?.findIndex(p => p.id.toString() === projectId) ?? -1;
        if (projectIndex === -1) return undefined;
        const connectionId = formValues[projectIndex]?.projectConnectionId;
        if (!connectionId) return undefined;
        const ownProject = crmProjects.find(cp => cp.id === connectionId);
        if (ownProject) return ownProject;
        const projectData = projectsList![projectIndex];
        const crmProjectData = Array.isArray(projectData.crm_project)
            ? projectData.crm_project.find(cp => cp.id === connectionId)
            : projectData.crm_project?.id === connectionId ? projectData.crm_project : null;
        const foreignCrmName: string | undefined = crmProjectData?.crm?.name;
        return { id: connectionId, name: 'Connected via another CRM', isForeign: true, foreignCrmName };
    };

    const sortedCrmProjects = [...crmProjects].sort((a, b) =>
        sortAsc
            ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            : new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const displayedProjects = (projectsList ?? [])
        .filter(p => !showWithoutConnectionsOnly || !getAssignment(p.id.toString()))
        .sort((a, b) =>
            sortAsc
                ? new Date(a?.created_at ?? 0).getTime() - new Date(b?.created_at ?? 0).getTime()
                : new Date(b?.created_at ?? 0).getTime() - new Date(a?.created_at ?? 0).getTime()
        );

    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId !== 'crm-projects' || !destination.droppableId.startsWith('project-')) return;

        const projectId = destination.droppableId.replace('project-', '');
        const crmProject = crmProjects.find(cp => cp.id.toString() === draggableId);
        if (!crmProject) return;

        const projectIndex = projectsList?.findIndex(p => p.id.toString() === projectId) ?? -1;
        if (projectIndex !== -1) {
            form.setValue(`${projectIndex}.projectConnectionId`, Number(crmProject.id));
            form.setValue(`${projectIndex}.projectId`, projectId);
        }
    };

    const removeAssignment = (projectId: string) => {
        const projectIndex = projectsList?.findIndex(p => p.id.toString() === projectId) ?? -1;
        if (projectIndex !== -1) {
            form.setValue(`${projectIndex}.projectConnectionId`, undefined);
        }
    };

    return (
        <DragDropContext onDragEnd={handleDragEnd}>
            <ControlsBar
                sortAsc={sortAsc}
                onSortToggle={() => setSortAsc(prev => !prev)}
                showWithoutConnectionsOnly={showWithoutConnectionsOnly}
                onFilterChange={setShowWithoutConnectionsOnly}
                filterLabel="Projects without connections only"
            />
            <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="flex flex-col gap-1">
                    <Label>Project Connections</Label>
                    <BorderBox className="flex-1 block mt-1 bg-white">
                        <ScrollArea className="h-[55vh] pe-4">
                            <Droppable droppableId="crm-projects">
                                {(provided) => (
                                    <div
                                        className="flex flex-col gap-2"
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {sortedCrmProjects.map((crmProject, index) => (
                                            <Draggable
                                                key={crmProject.id.toString()}
                                                draggableId={crmProject.id.toString()}
                                                index={index}
                                            >
                                                {(provided, snapshot) => {
                                                    const item = (
                                                        <DraggableCrmSystemItem
                                                            provided={provided}
                                                            isDragging={snapshot.isDragging}
                                                            crmSystem={crmSystem}
                                                            borderBoxClassName="w-full"
                                                        >
                                                            <div className="flex flex-col gap-0.5 flex-grow min-w-0">
                                                                <span className="font-medium">{crmProject.name}</span>
                                                                {crmProject.description && (
                                                                    <span className="text-sm line-clamp-1 text-muted-foreground">{crmProject.description}</span>
                                                                )}
                                                            </div>
                                                        </DraggableCrmSystemItem>
                                                    );
                                                    return snapshot.isDragging
                                                        ? createPortal(item, document.body)
                                                        : item;
                                                }}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </ScrollArea>
                    </BorderBox>
                </div>

                <div className="flex flex-col gap-1">
                    <Label><OrganizationTypeWordingResolver capitalize plural /></Label>
                    <BorderBox className="block mt-1 bg-white">
                        <ScrollArea className="h-[55vh] pe-4">
                            <div className="flex flex-col w-full gap-2">
                                {displayedProjects.map(project => (
                                    <ProjectConnectionDropTarget
                                        key={project.id}
                                        project={project}
                                        assignment={getAssignment(project.id.toString())}
                                        onRemove={() => removeAssignment(project.id.toString())}
                                        crmSystem={crmSystem}
                                    />
                                ))}
                            </div>
                        </ScrollArea>
                    </BorderBox>
                </div>
            </div>
        </DragDropContext>
    );
}

interface CrmProjectDropTargetProps {
    project: UseProjectsListReturnData[number];
    assignment?: AssignmentDisplay;
    onRemove: () => void;
    crmSystem: CrmSystemIds;
}

function ProjectConnectionDropTarget({ project, assignment, onRemove, crmSystem }: Readonly<CrmProjectDropTargetProps>) {
    return (
        <BorderBox size='sm' className="flex flex-col items-start w-full">
            <Label className="text-sm">{project.name}</Label>
            <DroppableContainer
                droppableId={`project-${project.id}`}
                placeholder="Drop CRM project here to connect"
                borderBoxClassName="min-h-8 max-h-10"
            >
                {assignment && (
                    <BorderBox
                        className="flex items-center gap-2 border-l-4"
                        style={!assignment.isForeign ? { borderLeftColor: crmRegistry.getSystemById(crmSystem)?.branding.color } : undefined}
                        size='xs'
                    >
                        {assignment.isForeign && (
                            <TooltipProvider>
                                <TooltipButton
                                    size='action-xs'
                                    variant='ghost'
                                    tooltipContent={assignment.foreignCrmName ? `Connected via ${assignment.foreignCrmName}` : 'Connected via another CRM'}
                                    className="text-amber-500 hover:text-amber-600 hover:bg-amber-50"
                                >
                                    <TriangleAlert size={14} />
                                </TooltipButton>
                            </TooltipProvider>
                        )}
                        <span className={`flex-grow font-medium${assignment.isForeign ? ' text-muted-foreground' : ''}`}>
                            {assignment.isForeign
                                ? (assignment.foreignCrmName ? `Connected via ${assignment.foreignCrmName}` : 'Connected via another CRM')
                                : assignment.name}
                        </span>
                        <TooltipProvider>
                            <TooltipButton
                                size='action-xs'
                                variant='destructive'
                                tooltipContent="Remove connection"
                                onClick={onRemove}
                            >
                                <X size={12} />
                            </TooltipButton>
                        </TooltipProvider>
                    </BorderBox>
                )}
            </DroppableContainer>
        </BorderBox>
    );
}

type CrmConfig = Tables<'crm_config'>;

function ConnectionsWizardSurveysTabContent({ projectsList, form, connectionWithProjectsAndSurveysConnections }: Readonly<ConnectionsWizardTabContentProps>) {
    const formValues = form.watch();

    const projectsWithConnections = projectsList?.filter(project => {
        const projectIndex = projectsList.findIndex(p => p.id === project.id);
        const connectionId = formValues[projectIndex]?.projectConnectionId;
        if (!connectionId) return false;
        return connectionWithProjectsAndSurveysConnections.crm_project.some(cp => cp.id === connectionId);
    });

    const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

    const selectedProjectIndex = projectsList?.findIndex(p => p.id.toString() === selectedProjectId) ?? -1;
    const selectedProject = projectsWithConnections?.find(p => p.id.toString() === selectedProjectId);

    const assignedCrmProject = connectionWithProjectsAndSurveysConnections.crm_project.find(
        cp => cp.id === formValues[selectedProjectIndex]?.projectConnectionId
    );
    const crmConfigs = assignedCrmProject?.crm_config ?? [];
    const surveysForSelectedProject = selectedProject?.survey ?? [];
    const crmSystem = connectionWithProjectsAndSurveysConnections.crm.crm_system;

    const [sortAsc, setSortAsc] = useState(false);
    const [showWithoutConnectionsOnly, setShowWithoutConnectionsOnly] = useState(false);

    const getSurveyAssignment = (surveyId: string): CrmConfig | undefined => {
        if (selectedProjectIndex === -1) return undefined;
        const surveyIndex = surveysForSelectedProject.findIndex(s => s.id.toString() === surveyId);
        if (surveyIndex === -1) return undefined;
        const connectionId = formValues[selectedProjectIndex]?.surveys?.[surveyIndex]?.surveyConnectionId;
        if (!connectionId) return undefined;
        return crmConfigs.find(cc => cc.id === connectionId);
    };

    const sortedCrmConfigs = [...crmConfigs].sort((a, b) =>
        sortAsc
            ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
            : new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );

    const displayedSurveys = surveysForSelectedProject
        .filter(s => !showWithoutConnectionsOnly || !getSurveyAssignment(s.id.toString()))
        .sort((a, b) =>
            sortAsc
                ? new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
                : new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );

    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId } = result;
        if (!destination) return;
        if (source.droppableId !== 'crm-configs' || !destination.droppableId.startsWith('survey-')) return;

        const surveyId = destination.droppableId.replace('survey-', '');
        const crmConfig = crmConfigs.find(cc => cc.id.toString() === draggableId);
        if (!crmConfig || selectedProjectIndex === -1) return;

        const surveyIndex = surveysForSelectedProject.findIndex(s => s.id.toString() === surveyId);
        if (surveyIndex !== -1) {
            const currentSurveys = form.getValues(`${selectedProjectIndex}.surveys`) || {};
            form.setValue(`${selectedProjectIndex}.surveys`, {
                ...currentSurveys,
                [surveyIndex]: { surveyConnectionId: crmConfig.id, surveyId },
            });
        }
    };

    const removeSurveyAssignment = (surveyId: string) => {
        if (selectedProjectIndex === -1) return;
        const surveyIndex = surveysForSelectedProject.findIndex(s => s.id.toString() === surveyId);
        if (surveyIndex !== -1) {
            const currentSurveys = { ...form.getValues(`${selectedProjectIndex}.surveys`) };
            delete currentSurveys[surveyIndex];
            form.setValue(`${selectedProjectIndex}.surveys`, currentSurveys);
        }
    };

    return (
        <div className="flex flex-col gap-3 mt-2">
            <div className="flex flex-col gap-1">
                <Label>Select <OrganizationTypeWordingResolver capitalize /></Label>
                {projectsWithConnections?.length === 0
                    ? (
                        <BorderBox variant='danger' size='sm'>
                            No <OrganizationTypeWordingResolver plural /> with assigned connections. Please assign a project connection in the "Assign Project Connections" tab first.
                        </BorderBox>
                    )
                    : (

                        <Select value={selectedProjectId ?? ''} onValueChange={setSelectedProjectId}>
                            <SelectTrigger>
                                <SelectValue placeholder={<span>Select <OrganizationTypeWordingResolver capitalize /></span>} />
                            </SelectTrigger>
                            <SelectContent>
                                {projectsWithConnections?.map(project => (
                                    <SelectItem key={project.id} value={project.id.toString()}>
                                        {project.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )}
            </div>
            {selectedProject ? (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <ControlsBar
                        sortAsc={sortAsc}
                        onSortToggle={() => setSortAsc(prev => !prev)}
                        showWithoutConnectionsOnly={showWithoutConnectionsOnly}
                        onFilterChange={setShowWithoutConnectionsOnly}
                        filterLabel="Surveys without connections only"
                    />
                    <div className="grid grid-cols-2 gap-2">
                        <div className="flex flex-col gap-1">
                            <Label>CRM Configurations</Label>
                            <BorderBox className="flex-1 block mt-1 bg-white">
                                <ScrollArea className="h-[45vh] pe-4">
                                    <Droppable droppableId="crm-configs">
                                        {(provided) => (
                                            <div
                                                className="flex flex-col gap-2"
                                                ref={provided.innerRef}
                                                {...provided.droppableProps}
                                            >
                                                {sortedCrmConfigs.map((crmConfig, index) => (
                                                    <Draggable
                                                        key={crmConfig.id.toString()}
                                                        draggableId={crmConfig.id.toString()}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => {
                                                            const item = (
                                                                <DraggableCrmSystemItem
                                                                    provided={provided}
                                                                    isDragging={snapshot.isDragging}
                                                                    crmSystem={crmSystem}
                                                                    borderBoxClassName="w-full"
                                                                >
                                                                    <div className="flex flex-col gap-0.5 flex-grow min-w-0">
                                                                        <span className="font-medium">{crmConfig.name}</span>
                                                                        {crmConfig.description && (
                                                                            <span className="text-sm line-clamp-1 text-muted-foreground">{crmConfig.description}</span>
                                                                        )}
                                                                    </div>
                                                                </DraggableCrmSystemItem>
                                                            );
                                                            return snapshot.isDragging
                                                                ? createPortal(item, document.body)
                                                                : item;
                                                        }}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                                {crmConfigs.length === 0 && (
                                                    <p className="p-2 text-sm text-center text-muted-foreground">
                                                        No CRM configurations for this project connection
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </Droppable>
                                </ScrollArea>
                            </BorderBox>
                        </div>

                        <div className="flex flex-col gap-1">
                            <Label>Surveys</Label>
                            <BorderBox className="block mt-1 bg-white">
                                <ScrollArea className="h-[45vh] pe-4">
                                    <div className="flex flex-col w-full gap-2">
                                        {displayedSurveys.map(survey => (
                                            <SurveyConnectionDropTarget
                                                key={survey.id}
                                                survey={survey}
                                                assignment={getSurveyAssignment(survey.id.toString())}
                                                onRemove={() => removeSurveyAssignment(survey.id.toString())}
                                                crmSystem={crmSystem}
                                            />
                                        ))}
                                        {surveysForSelectedProject.length === 0 && (
                                            <p className="p-2 text-sm text-center text-muted-foreground">
                                                No surveys for this project
                                            </p>
                                        )}
                                    </div>
                                </ScrollArea>
                            </BorderBox>
                        </div>
                    </div>
                </DragDropContext>
            ) : (
                <BorderBox className='h-[45vh] grid place-items-center bg-white'>
                    <p className="py-6 text-sm text-center text-muted-foreground">
                        Select a <OrganizationTypeWordingResolver /> above to assign survey connections
                    </p>
                </BorderBox>

            )}
        </div>
    );
}

interface SurveyConnectionDropTargetProps {
    survey: NonNullable<UseProjectsListReturnData[number]['survey']>[number];
    assignment?: CrmConfig;
    onRemove: () => void;
    crmSystem: CrmSystemIds;
}

function SurveyConnectionDropTarget({ survey, assignment, onRemove, crmSystem }: Readonly<SurveyConnectionDropTargetProps>) {
    return (
        <BorderBox size='sm' className="flex flex-col items-start w-full">
            <Label className="text-sm">{survey.name}</Label>
            <DroppableContainer
                droppableId={`survey-${survey.id}`}
                placeholder="Drop CRM configuration here to connect"
                borderBoxClassName="min-h-8 max-h-10"
            >
                {assignment && (
                    <BorderBox
                        className="flex items-center gap-2 border-l-4"
                        style={{ borderLeftColor: crmRegistry.getSystemById(crmSystem as CrmSystemIds)?.branding.color }}
                        size='xs'
                    >
                        <span className="flex-grow font-medium">{assignment.name}</span>
                        <TooltipProvider>
                            <TooltipButton
                                size='action-xs'
                                variant='destructive'
                                tooltipContent="Remove connection"
                                onClick={onRemove}
                            >
                                <X size={12} />
                            </TooltipButton>
                        </TooltipProvider>
                    </BorderBox>
                )}
            </DroppableContainer>
        </BorderBox>
    );
}

interface ControlsBarProps {
    sortAsc: boolean;
    onSortToggle: () => void;
    showWithoutConnectionsOnly: boolean;
    onFilterChange: (value: boolean) => void;
    filterLabel: string;
}

function ControlsBar({ sortAsc, onSortToggle, showWithoutConnectionsOnly, onFilterChange, filterLabel }: Readonly<ControlsBarProps>) {
    return (
        <div className="flex items-center justify-between mb-2">
            <Button 
            type="button" 
            variant="outline" 
            size="sm" 
            onClick={onSortToggle}
            >
                <ArrowUpDown size={14} className="mr-1" />
                Created at: {sortAsc ? 'Oldest first' : 'Newest first'}
            </Button>
            <Button
                className="flex items-center gap-2 text-sm font-normal cursor-pointer"
                onClick={(e) => {
                    if(e.target !== e.currentTarget) return;
                    onFilterChange(!showWithoutConnectionsOnly);
                }}
                variant='outline'
                size='sm'
                type='button'
            >
                <Checkbox
                    id="controls-bar-filter"
                    checked={showWithoutConnectionsOnly}
                    className='size-4'
                />
                    {filterLabel}
            </Button>
        </div>
    );
}

export function ConnectionsWizardDialogForProjectsWithoutConnections({ ...props }: Readonly<Omit<ConnectionsWizardDialogProps, 'projectsList'>>) {
    const { organization } = useOrganizationStore();
    const { data: projectsListWithoutConnectionsData } = useProjectsList({
        orgId: organization?.id,
        select: (data) => data?.filter(project => project.crm_project_id === null && project.crm_system === props?.connectionWithProjectsAndSurveysConnections?.crm.crm_system),
    })
    return (
        <ConnectionsWizardDialog
            projectsList={projectsListWithoutConnectionsData}
            {...props}
        />
    )
}

export function ConnectionsWizardDialogForCrmById({ ...props }: Readonly<(Omit<ConnectionsWizardDialogProps, 'projectsList' | 'connectionWithProjectsAndSurveysConnections'>) & { crmId: Tables<'crm'>['id'] }>) {
    const { data: crmData, isLoading } = useGetCrmById(props.crmId)
    const { organization } = useOrganizationStore();
    const { data: projectsListWithoutConnectionsData } = useProjectsList({
        orgId: organization?.id,
        select: (data) => data?.filter(project => project.crm_system === crmData?.crm_system),
    })

    const connectionWithProjectsAndSurveysConnections: CreateCrmWithProjectsResult | undefined = crmData
        ? {
            crm: crmData,
            crm_project: crmData.crm_project ?? [],
        }
        : undefined;

    return (
        <ConnectionsWizardDialog
            projectsList={projectsListWithoutConnectionsData}
            connectionWithProjectsAndSurveysConnections={connectionWithProjectsAndSurveysConnections}
            isLoading={isLoading}
            {...props}
        />
    )
}

export const connectionsWizardDialog = dialog(ConnectionsWizardDialog)
export const connectionsWizardDialogForProjectsWithoutConnections = dialog(ConnectionsWizardDialogForProjectsWithoutConnections)

export const connectionsWizardDialogForCrmById = dialog(ConnectionsWizardDialogForCrmById)